import { Box, Card, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import DefaultButton from "../simple/DefaultButton";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import BasicCourseDetailPanel from "../simple/BasicCourseDetailPanel";
import TeamIcon from "../../assets/icons/team.svg";
import BookIcon from "../../assets/icons/book.svg";
import BookClosedIcon from "../../assets/icons/book-closed.svg";
import BarChartIcon from "../../assets/icons/bar-chart.svg";
import { useNavigate } from "react-router-dom";
import BoltIcon from "@mui/icons-material/Bolt";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import Alert from "@mui/material/Alert";
import ProgressBar from "../simple/ProgressBar";
import UnlockModal from "../UnlockModal/UnlockModal";

export default function ProgramInfoCard({
  trainingProgram,
  totalSteps,
  isMobile,
  isTrial = false,
  daysLeft,
  enrollment,
}) {
  const theme = useTheme();
  const [scoringStats, setScoringStats] = React.useState({
    withScoring: 0,
    withoutScoring: 0,
  });
  const [unlockOpen, setUnlockOpen] = useState(false);
  const navigate = useNavigate();

  let nextScenario = null;

  if (trainingProgram?.chapters?.length) {
    for (const chapter of trainingProgram.chapters) {
      const chapterScenarios = chapter.scenarios || [];
      for (const scenario of chapterScenarios) {
        if (!scenario.completed && !scenario.locked) {
          nextScenario = scenario;
          break;
        }
      }
      if (nextScenario) break;
    }
  }

  useEffect(() => {
    if (!trainingProgram) return;
    if (!trainingProgram.chapters) return;
    if (!trainingProgram.chapters.some(chapter => chapter.scenarios)) return;

    let withScoring = 0;
    let withoutScoring = 0;

    trainingProgram.chapters.forEach((chapter) => {
      chapter.scenarios.forEach((scenario) => {
        if (scenario?.has_scoring_engine) {
          withScoring++;
        } else {
          withoutScoring++;
        }
      });
    });

    setScoringStats({ withScoring, withoutScoring });
  }, [trainingProgram]);

  if (!trainingProgram) {
    return (
      <Card
        sx={{
          mb: theme.customSpacing["4xl"],
          padding: theme.containers.paddingDesktop,
          color: theme.palette.text.secondary,
          border: `2px solid ${theme.palette.border.tertiary}`,
          borderRadius: theme.shape.radius,
          textAlign: "center",
          backgroundColor: "transparent",
        }}
      >
        <Typography sx={{ fontWeight: theme.typography.fontWeightMedium }}>
          Your program is still loading...
        </Typography>
        <Typography sx={{ color: theme.palette.text.gray }}>
          If this takes too long, please refresh the page.
        </Typography>
      </Card>
    );
  }

  return (
    <Box
      id="training-program-overview"
      sx={{
        display: "flex",
        flexDirection: "row",
        color: theme.palette.text.secondary,
        maxHeight: "382px",
        marginBottom: "20px",
        [theme.breakpoints.down("md")]: {
          flexWrap: "wrap",
          maxHeight: "none",
        },
        "@media (max-width:1080px)": {
          flexWrap: "wrap",
          maxHeight: "none",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "40%",
          flexShrink: 0,
          backgroundColor: "white",
          backgroundImage: `url(${trainingProgram?.image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          borderRadius: theme.shape.radius.md,
          justifyContent: "flex-end",
          mr: theme.customSpacing["xl"],
          [theme.breakpoints.down("md")]: {
            width: "100%",
            marginRight: 0,
            marginBottom: "20px",
            minHeight: "200px",
          },
          "@media (max-width:1080px)": {
            width: "100%",
            marginRight: 0,
            marginBottom: "20px",
            minHeight: "250px",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            zIndex: 1,
            background:
              "linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(0,0,0,0.65) 100%)",
            borderRadius: theme.shape.radius.md,
            padding: isMobile ? "1.25rem" : theme.containers.paddingDesktop,
          }}
        >
          {isTrial && (
            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
              <Box sx={{
                display: "inline-flex", alignItems: "center", gap: 0.5,
                px: 1.25, py: 0.5, borderRadius: 999,
                background: "linear-gradient(95deg, #01ECFF, #962EFF)",
                fontSize: 11, fontWeight: 700, color: "#030039",
              }}>
                <BoltIcon sx={{ fontSize: 12 }} /> Free Trial
              </Box>
              {trainingProgram.category && (
                <Box sx={{
                  display: "inline-flex", alignItems: "center",
                  px: 1.25, py: 0.5, borderRadius: 999,
                  background: "rgba(0,0,0,0.5)", border: "1px solid rgba(255,255,255,0.2)",
                  fontSize: 11, fontWeight: 600, color: "#fff",
                }}>
                  {trainingProgram.category}
                </Box>
              )}
            </Box>
          )}
          <Typography
            sx={{
              color: theme.palette.text.primary,
              fontFamily: "Poppins",
              fontSize: "clamp(0.75rem, 0.75rem + 2vw, 1.3rem)",
              fontStyle: "normal",
              fontWeight: theme.typography.fontWeightMedium,
              lineHeight: "1.2",
              mb: "10px",
            }}
          >
            {trainingProgram?.name}
          </Typography>
          <Typography
            sx={{
              color: theme.palette.text.primary,
              fontFamily: "Poppins",
              fontSize: theme.typography.textXS.fontSize,
              fontStyle: "normal",
              fontWeight: theme.typography.fontWeightRegular,
              lineHeight: "1.2",
            }}
          >
            {trainingProgram.author
              ? `A Program By ${trainingProgram.author} | `
              : null}
            {trainingProgram.release_date
              ? `Released ${trainingProgram.release_date}`
              : null}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          backgroundColor: theme.palette.background.primary,
          justifyContent: "space-evenly",
          color: theme.palette.text.primary,
        }}
      >
        <Typography
          sx={{
            fontWeight: theme.typography.fontWeightMedium,
            fontSize: theme.typography.textXS.fontSize,
            textTransform: "uppercase",
            display: isMobile ? "none" : "block",
            mb: `${theme.customSpacing.lg}`,
          }}
        >
          Program Overview
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            mb: `${theme.customSpacing["2xl"]}`,
            gap: theme.customSpacing["md"],
          }}
        >
        {trainingProgram?.description && (
          <Typography
            variant="body2"
            sx={{
              fontSize: theme.typography.textXS.fontSize,
              lineHeight: theme.typography.textXS.lineHeight,
              color: theme.palette.text.gray,
              whiteSpace: "pre-wrap",
            }}
          >
            {trainingProgram?.description}
          </Typography>
        )}
        {trainingProgram?.read_more_url && (
          <Typography
            variant="body2"
            sx={{
              fontSize: theme.typography.textXS.fontSize,
              lineHeight: theme.typography.textXS.lineHeight,
              color: theme.palette.text.brand_secondary,
            }}
          >
            Read More:
            <a
              href={trainingProgram.read_more_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: theme.palette.text.primary, marginLeft: "5px" }}
            >
              {trainingProgram.read_more_url}
            </a>
          </Typography>
        )}
        </Box>
        <Box
          sx={{
            display: "flex",
            padding: 0,
            alignItems: "flex-start",
            gap: theme.customSpacing["lg"],
            alignSelf: "stretch",
            flexWrap: "wrap",
            mb: "20px",
          }}
        >
          <BasicCourseDetailPanel
            title={"Difficulty Level"}
            details={
              trainingProgram.difficulty_level
                ? trainingProgram.difficulty_level.charAt(0).toUpperCase() +
                  trainingProgram.difficulty_level.slice(1)
                : "Beginner"
            }
            icon={<img src={BarChartIcon} alt="Bar Charts" />}
          />
          <BasicCourseDetailPanel
            title={"Topics"}
            details={totalSteps}
            icon={<img src={BookClosedIcon} alt="" />}
          />
          <BasicCourseDetailPanel
            title={
              isMobile ? "Skill Sessions" : "Skill Development Sessions"
            }
            details={scoringStats.withoutScoring.toString()}
            icon={<img src={BookIcon} alt="Books" />}
          />
          <BasicCourseDetailPanel
            title={isMobile ? "Simulations" : "Simulations"}
            details={scoringStats.withScoring.toString()}
            icon={<img src={TeamIcon} alt="Team" />}
          />
        </Box>
        {isTrial ? (
          <>
            {/* Free trial active status box */}
            <Box sx={{
              display: "flex", alignItems: "center", gap: 1.5,
              px: 2, py: 1.5, mb: 2, borderRadius: theme.shape.radius.md,
              background: "rgba(1,236,255,0.06)",
              border: "1px solid rgba(1,236,255,0.20)",
            }}>
              <BoltIcon sx={{ color: "#01ECFF", fontSize: 20, flexShrink: 0 }} />
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: theme.palette.text.primary }}>
                  Free trial active
                </Typography>
                {daysLeft != null && (
                  <Typography sx={{ fontSize: 13, color: "#01ECFF" }}>
                    · {daysLeft} day{daysLeft !== 1 ? "s" : ""} remaining
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Progress section */}
            {enrollment && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: theme.palette.text.brand_secondary }}>
                    Program Progress
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: theme.palette.text.primary }}>
                    {enrollment.overallProgress}% completed
                  </Typography>
                </Box>
                <Box sx={{
                  height: 6, borderRadius: 999,
                  background: "rgba(255,255,255,0.10)", overflow: "hidden", mb: 1,
                }}>
                  <Box sx={{
                    height: "100%", width: `${enrollment.overallProgress}%`,
                    background: "linear-gradient(90deg, #01ECFF, #962EFF)",
                    borderRadius: 999, transition: "width 400ms ease",
                  }} />
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary }}>
                    📖 {enrollment.sessionsCompleted} / {enrollment.sessionsTotal} Skill Sessions
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary }}>
                    👥 {enrollment.simulationsCompleted} / {enrollment.simulationsTotal} Simulations
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Actions */}
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <DefaultButton
                wideOnMobile
                size="sm"
                onClick={() => nextScenario && navigate(`/dashboard/simulate/${nextScenario.id}`)}
                endIcon={<ArrowForwardRoundedIcon />}
                disabled={!nextScenario}
                sx={{ flex: 1 }}
              >
                Continue
              </DefaultButton>
              <DefaultButton
                wideOnMobile
                size="sm"
                variant="outlined"
                onClick={() => setUnlockOpen(true)}
                startIcon={<LockOpenIcon sx={{ fontSize: 16 }} />}
              >
                Unlock full program
              </DefaultButton>
            </Box>
            <UnlockModal open={unlockOpen} onClose={() => setUnlockOpen(false)} programId={trainingProgram?.id} />
          </>
        ) : nextScenario === null ? (
          <Alert variant="filled" severity="success">
            Course completed
          </Alert>
        ) : (
          <DefaultButton
            wideOnMobile
            size="sm"
            onClick={() => navigate(`/dashboard/simulate/${nextScenario.id}`)}
            endIcon={<ArrowForwardRoundedIcon />}
          >
            Up Next: {nextScenario?.name}
          </DefaultButton>
        )}
        {!isTrial && (
          <>
            <Box sx={{ height: theme.customSpacing["2xl"] }} />
            <ProgressBar trainingProgram={trainingProgram} />
          </>
        )}
      </Box>
    </Box>
  );
}
